import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { getSummaryBillApi, exportSummaryBillApi, exportSummaryBillPDF } from "../services/reports.service";
import { reportDayEndISO, reportDayStartISO } from "../utils/reportDateRange";
import { getUserApi } from "../services/user.service";
import { useGetApi } from "../hooks/useGetApi";
import { DEFAULT_LIMIT, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "serialNumber",
  },
  {
    id: 2,
    label: "Particular ",
    value: "particular",
  },
  {
    id: 3,
    label: "Details Of Charges",
    value: "detailsOfCharges",
  },
  {
    id: 4,
    label: "Provider",
    value: "provider",
  },
  {
    id: 5,
    label: "Qty",
    value: "quantity",
  },
  {
    id: 6,
    label: "Amount",
    value: "amount",
  },
];

// Template options for Summary Bill Report
const templateOptions = [
  { label: "All Bills Template", value: "allBills" },
  { label: "OPD Bills Template", value: "opdBills" },
  { label: "IPD Bills Template", value: "ipdBills" },
  { label: "Pharmacy Bills Template", value: "pharmacyBills" },
];

// Visit Type options
const visitTypeOptions = [
  { label: "OPD", value: "OPD" },
  { label: "IPD", value: "IPD" },
  { label: "Outsider", value: "Outsider" },
  { label: "Pharmacy", value: "Pharmacy" },
];

// User options will be fetched from API

const paymentFilterList = [
  { label: "Invoice Payment", value: "InvoicePayment" },
  { label: "Self Payment", value: "SelfPayment" },
  { label: "Refund", value: "Refund" },
  { label: "IPD Advance", value: "IPDAdvance" },
];

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

export default function ReportsSummaryOfBill() {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate(-1);
  };
  
  // Fetch users for dropdown
  const {
    dataList: users,
    isLoading: usersLoading,
  } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000, page: 1 },
    dataKey: "users",
  });

  // Transform users to options format
  const userOptions = useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    return users.map(user => ({
      label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User',
      value: user._id
    }));
  }, [users]);
  
  // State management
  const [formData, setFormData] = useState({
    searchTemplate: null,
    startDate: null,
    endDate: null,
    visitType: null,
    billGeneratedBy: null,
    billDetails: false,
    paymentDetails: false,
  });
  
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  
  // Pagination state - default to 10 rows per page like Cash Collection page
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    serialNumber: true,
    particular: true,
    detailsOfCharges: true,
    provider: true,
    quantity: true,
    amount: true,
  });

  // Menu anchor states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Summary Bill Report',
        text: `Summary Bill Report - ${totalCount} records`,
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle download menu
  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  // Handle PDF export
  const handlePDFExport = async () => {
    try {
      setIsExporting(true);
      
      // Prepare export parameters - matching fetchReportData params
      const exportParams = {
        startDate: reportDayStartISO(formData.startDate),
        endDate: reportDayEndISO(formData.endDate),
        visitType: formData.visitType?.value,
        billGeneratedBy: formData.billGeneratedBy?.value,
        page: 1,
        limit: 100000, // Export all records for PDF
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      // Define columns for PDF export with proper labels (matching headLabel order)
      const columns = headLabel.filter(col => visibleColumns[col.value]).map(col => ({
        header: col.label,
        dataKey: col.value,
      }));

      // Prepare date range for PDF header
      const dateRange = formData.startDate && formData.endDate ? {
        startDate: reportDayStartISO(formData.startDate),
        endDate: reportDayEndISO(formData.endDate),
      } : null;

      const result = await exportSummaryBillPDF(exportParams, columns, dateRange);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to PDF!`);
      } else {
        throw new Error('PDF export failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(`PDF export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleDownloadClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        handlePDFExport();
        break;
      case 'Report Mail Send Scheduler':
        // TODO: Implement report mail scheduler when feature is available
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        // TODO: Implement report scheduler when feature is available
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };

  // Fetch report data - accepts optional parameters to avoid stale closure issues
  const fetchReportData = async (currentPage = page, currentRowsPerPage = rowsPerPage, showToast = false) => {
    try {
      setLoading(true);
      
      // Map frontend form data to backend API parameters
      const params = {
        startDate: reportDayStartISO(formData.startDate),
        endDate: reportDayEndISO(formData.endDate),
        visitType: formData.visitType?.value,
        billGeneratedBy: formData.billGeneratedBy?.value,
        page: currentPage + 1, // Backend uses 1-based pagination
        limit: currentRowsPerPage,
      };

      // Remove undefined/empty values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      console.log('Fetching summary bill data with params:', params);
      const response = await getSummaryBillApi(params);
      console.log('API Response:', response);

      if (response?.data) {
        let dataArray = [];
        if (response.data.items && Array.isArray(response.data.items)) {
          dataArray = response.data.items;
        } else if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else {
          dataArray = [];
        }
        
        // Set total count for pagination
        setTotalCount(response.data.total || 0);
        setReportData(dataArray);
      
      // Only show success message when user explicitly searches
      if (showToast) {
        toast.success("Report data loaded successfully");
        }
      } else {
        setReportData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error("Failed to fetch report data");
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    if (e) {
    e.preventDefault();
    }
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.startDate) {
      errorFields.push('startDate');
    }
    if (!formData.endDate) {
      errorFields.push('endDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    console.log('Searching with form data:', formData);
    isSearchingRef.current = true; // Mark that we're performing a search
    setPage(0); // Reset to first page on new search
    setHasSearched(true); // Mark that search has been performed
    // Fetch with page 0, current rowsPerPage, and showToast true for success message
    fetchReportData(0, rowsPerPage, true);
  };

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Prepare export parameters - matching fetchReportData params
      const exportParams = {
        startDate: reportDayStartISO(formData.startDate),
        endDate: reportDayEndISO(formData.endDate),
        visitType: formData.visitType?.value,
        billGeneratedBy: formData.billGeneratedBy?.value,
        page: 1,
        limit: 100000, // Export all records
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      // Define column order for Excel export (matching headLabel order to fix S.No and Quantity swap)
      const columnOrder = headLabel.filter(col => visibleColumns[col.value]).map(col => col.value);

      // Import the exportSummaryBillApi function and update it to accept columnOrder
      // For now, we'll need to update the service function call
      const result = await exportSummaryBillApi(exportParams, columnOrder);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Refetch data when pagination changes (only if user has already searched)
  // Use refs to track previous values to detect actual changes
  const prevPageRef = useRef(page);
  const prevRowsPerPageRef = useRef(rowsPerPage);
  const isSearchingRef = useRef(false);
  
  useEffect(() => {
    // Skip if no search has been performed yet
    if (!hasSearched) {
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return;
    }
    
    // Skip if we're currently performing a search (handleSearch is running)
    if (isSearchingRef.current) {
      isSearchingRef.current = false;
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return; // handleSearch already fetched the data
    }
    
    // Check if page or rowsPerPage actually changed
    const pageChanged = prevPageRef.current !== page;
    const rowsPerPageChanged = prevRowsPerPageRef.current !== rowsPerPage;
    
    // Only refetch if:
    // 1. User has performed a search (hasSearched flag)
    // 2. Page or rowsPerPage actually changed (not just a re-render)
    if (hasSearched && (pageChanged || rowsPerPageChanged)) {
      console.log('Pagination changed - page:', page, 'rowsPerPage:', rowsPerPage, 'prevPage:', prevPageRef.current, 'prevRowsPerPage:', prevRowsPerPageRef.current);
      // Call fetchReportData with current page and rowsPerPage values
      fetchReportData(page, rowsPerPage);
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    } else {
      // Update refs even if we don't fetch (to track current state)
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Breakup/Summary Of Bills - Charge Head Wise Report
            </Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, searchTemplate: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(newValue) => {
                        handleDateChange("startDate", newValue);
                        // Clear error when field is changed
                        if (errors.includes('startDate')) {
                          setErrors(errors.filter(e => e !== 'startDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('startDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date" 
                      value={formData.endDate}
                      onChange={(newValue) => {
                        handleDateChange("endDate", newValue);
                        // Clear error when field is changed
                        if (errors.includes('endDate')) {
                          setErrors(errors.filter(e => e !== 'endDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('endDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={visitTypeOptions}
                  value={formData.visitType}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, visitType: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visit Type"
                      name="visitType"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={userOptions}
                  value={formData.billGeneratedBy}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, billGeneratedBy: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  loading={usersLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bill Generated By"
                      name="billGeneratedBy"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="billDetails"
                      checked={formData?.billDetails}
                      onChange={handleChange}
                    />
                  }
                  label="Bill Details"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="paymentDetails"
                      checked={formData?.paymentDetails}
                      onChange={handleChange}
                    />
                  }
                  label="Payment Details"
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              mt: "20px",
              p: "20px",
            }}
          >
              {/* dynamic button */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {Array.isArray(reportData) ? reportData.length : 0} | Columns:{" "}
                    {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon 
                        sx={{
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps}>
                      <IconButton onClick={handleDownloadClick}>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* Filter Menu */}
                  <Menu
                    anchorEl={filterAnchorEl}
                    open={filterMenuOpen}
                    onClose={handleFilterClose}
                    PaperProps={{
                      style: {
                        maxHeight: 400,
                        padding: "8px 0",
                        width: "200px",
                      },
                    }}
                  >
                    <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                      Show/Hide Columns
                    </MenuItem>
                    <Divider />
                    {headLabel.map((column) => (
                      <MenuItem
                        key={column.id}
                        onClick={() => toggleColumn(column.value)}
                        sx={menuItemStyle}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ flex: 1 }}>{column.label}</Box>
                          {visibleColumns[column.value] && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>
                  {/* Download Menu */}
                  <Menu
                    anchorEl={downloadAnchorEl}
                    open={downloadMenuOpen}
                    onClose={handleDownloadClose}
                    slotProps={{
                      sx: {
                        borderRadius: "12px",
                        boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                        padding: "8px 0",
                        width: "250px",
                      },
                    }}
                  >
                    {menuItems.map((item, index) => (
                      <React.Fragment key={item}>
                        <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                          {item}
                        </MenuItem>
                        {index !== menuItems.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </Menu>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table aria-label="master ward table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {headLabel?.map((option) => 
                        visibleColumns[option.value] && (
                        <TableCell
                          key={option?.id}
                          sx={{ textAlign: option?.align || "left" }}
                        >
                          {option?.label}
                        </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !Array.isArray(reportData) || reportData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.map((row, rowIndex) => (
                        <TableRow key={row?._id || rowIndex}>
                          {headLabel?.map((column, index) => 
                            visibleColumns[column.value] && (
                            <TableCell key={column?.id}>
                                {column.value === 'serialNumber' 
                                  ? (page * rowsPerPage + rowIndex + 1)
                                  : (row?.[column?.value] || "-")
                                }
                            </TableCell>
                            )
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {totalCount > 0 && (
                <TablePagination
                  rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </Box>
        </Box>
      </Box>
    </Container>
  );
}
